/**
 * Request Cache and Deduplication Utility
 * 
 * Prevents duplicate API requests and caches responses for a configurable time.
 * This significantly improves performance by avoiding redundant network calls.
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

interface PendingRequest<T> {
  promise: Promise<T>;
  timestamp: number;
}

class RequestCache {
  private cache = new Map<string, CacheEntry<any>>();
  private pendingRequests = new Map<string, PendingRequest<any>>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes default

  /**
   * Generate a cache key from URL and params
   */
  private getCacheKey(url: string, params?: Record<string, any>): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${url}${paramString}`;
  }

  /**
   * Check if cache entry is still valid
   */
  private isValid(entry: CacheEntry<any>): boolean {
    return Date.now() < entry.expiresAt;
  }

  /**
   * Get cached data if available and valid
   */
  get<T>(url: string, params?: Record<string, any>): T | null {
    const key = this.getCacheKey(url, params);
    const entry = this.cache.get(key);

    if (entry && this.isValid(entry)) {
      console.log('📦 Cache hit:', url);
      return entry.data as T;
    }

    // Clean up expired entry
    if (entry) {
      this.cache.delete(key);
    }

    return null;
  }

  /**
   * Set cached data with TTL
   */
  set<T>(url: string, data: T, ttl: number = this.defaultTTL, params?: Record<string, any>): void {
    const key = this.getCacheKey(url, params);
    const now = Date.now();

    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + ttl,
    });
  }

  /**
   * Deduplicate requests - if same request is in flight, return existing promise
   */
  async deduplicate<T>(
    url: string,
    requestFn: () => Promise<T>,
    ttl: number = this.defaultTTL,
    params?: Record<string, any>
  ): Promise<T> {
    const key = this.getCacheKey(url, params);

    // Check cache first
    const cached = this.get<T>(url, params);
    if (cached !== null) {
      return cached;
    }

    // Check if request is already in flight
    const pending = this.pendingRequests.get(key);
    if (pending) {
      console.log('🔄 Request deduplication:', url);
      return pending.promise;
    }

    // Make new request
    const promise = requestFn()
      .then((data) => {
        // Cache the result
        this.set(url, data, ttl, params);
        // Clean up pending request
        this.pendingRequests.delete(key);
        return data;
      })
      .catch((error) => {
        // Clean up pending request on error
        this.pendingRequests.delete(key);
        throw error;
      });

    // Store pending request
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now(),
    });

    return promise;
  }

  /**
   * Invalidate cache for a specific URL
   */
  invalidate(url: string, params?: Record<string, any>): void {
    const key = this.getCacheKey(url, params);
    this.cache.delete(key);
    console.log('🗑️ Cache invalidated:', url);
  }

  /**
   * Invalidate all cache entries matching a pattern
   */
  invalidatePattern(pattern: string): void {
    const keys = Array.from(this.cache.keys());
    let count = 0;

    keys.forEach((key) => {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        count++;
      }
    });

    console.log(`🗑️ Cache invalidated (${count} entries):`, pattern);
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear();
    this.pendingRequests.clear();
    console.log('🗑️ All cache cleared');
  }

  /**
   * Clean up expired entries (call periodically)
   */
  cleanup(): void {
    const now = Date.now();
    let cleaned = 0;

    this.cache.forEach((entry, key) => {
      if (now >= entry.expiresAt) {
        this.cache.delete(key);
        cleaned++;
      }
    });

    // Clean up stale pending requests (older than 30 seconds)
    this.pendingRequests.forEach((pending, key) => {
      if (now - pending.timestamp > 30000) {
        this.pendingRequests.delete(key);
      }
    });

    if (cleaned > 0) {
      console.log(`🧹 Cache cleanup: ${cleaned} expired entries removed`);
    }
  }
}

// Singleton instance
export const requestCache = new RequestCache();

// Cleanup expired entries every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(() => requestCache.cleanup(), 5 * 60 * 1000);
}

// Cache TTL presets
export const CacheTTL = {
  SHORT: 1 * 60 * 1000,      // 1 minute
  MEDIUM: 5 * 60 * 1000,     // 5 minutes
  LONG: 15 * 60 * 1000,      // 15 minutes
  HOUR: 60 * 60 * 1000,      // 1 hour
  DAY: 24 * 60 * 60 * 1000,  // 24 hours
};



